import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import Range from './../Range'
import Chart from './../Chart'
import { Button } from '@arnsbogroup/react-voicearchive'

const Rate = ({ rate, supplierId, onChange, readOnly, accepted = false, custom = false }) => {
  const { id, parent_id, product, media, timing_level, currency, description, unit } = rate
  const currencySymbol = {
    SEK: "SEK",
    NOK: "NOK",
    EUR: "€"
  }[currency] ?? "€"

  const [loading, setLoading] = useState(false)
  const [changed, setChanged] = useState(false)
  const [ranges, setRanges] = useState(rate.ranges)
  const isNew = id.toString().startsWith('#')

  const onSave = () => {
    setLoading(true)

    fetch(`/rest/v2/supplier/${supplierId}/rates${isNew ? '' : `/${id}`}`, {
      credentials: 'same-origin',
      method: isNew ? 'POST' : 'PUT',
      body: JSON.stringify({
        ...rate,
        id: undefined,
        ranges ,
        parentId: parent_id,
        productId: product ? product.id : null,
        mediaId: media ? media.id : null,
        timingLevelId: timing_level ? timing_level.id : null,
      })
    })
      .then((response) => response.json())
      .then(({ data }) => onChange(id, data))
      .then(() => !isNew && setChanged(false))
      .then(() => !isNew && setLoading(false))
      .catch((error) => window.alert(error))
  }

  const onAmountChange = (index, amount) => {
    setChanged(true)
    setRanges((ranges) => Object.assign([], ranges, {
      [index]: { ...ranges[index], amount: parseFloat(amount) }
    }))
  }

  const onlyGraph = unit === 'WORDS'
    && ranges.length > 2
    && currency !== 'PERCENT'

  return (
    <div className="m-5 border border-gray-200">
      <div className="flex justify-between items-center">
        <div className="font-medium text-sm text-gray-700 truncate py-1 px-2 space-x-1">
          { product &&
            <span>
              <FormattedMessage {...messages[media ? 'paidAdvertising' : 'ownedMedia'] } />
            </span>
          }

          { media &&
            <FormattedMessage {...(messages[`mediaHint${media.uid}`] || messages.media) } tagName="span">
              { (hint) => (
                <span title={hint}>{ media.name }</span>
              ) }
            </FormattedMessage>
          }

          { timing_level &&
            <FormattedMessage {...(messages[`timingLevelHint${timing_level.id}`] || messages.timingLevel) } tagName="span">
              { (hint) => (
                <span title={hint}>{ timing_level.name }</span>
              ) }
            </FormattedMessage>
          }

          { description &&
            <span>{ description }</span>
          }
        </div>
        <div className="text-sm text-gray-500">
          { readOnly && !isNew && accepted ? (
            <span className="cursor-not-allowed px-2 space-x-2">
              {custom ? (
                <span><FormattedMessage {...messages.acceptedCustom} /></span>
              ) : (
                <span><FormattedMessage {...messages.acceptedStandard} /></span>
              )}
              <span className="w-2 h-2 rounded-full inline-block bg-green-400" />
            </span>
          ) : readOnly ? (
            <span className="cursor-not-allowed px-2">
              <FormattedMessage {...messages.required} />
            </span>
          ) : (
            loading ? (
              <span className="cursor-wait px-2">
                <FormattedMessage {...messages.loading} />
                <Icon icon={['fal', 'spinner-third']} className="text-xs animate-spin ml-1" />
              </span>
            ) : changed ? (
              <Button 
                size="sm"
                onClick={onSave} 
                className="w-32 truncate">
                <FormattedMessage {...messages.save} />
              </Button>
            ) : (isNew ? (
              <Button 
                size="sm"
                onClick={onSave} 
                state="positive"
                className="w-32 truncate">
                <FormattedMessage {...messages.accept} />
              </Button>
            ) : (
              <span className="px-2">
                <FormattedMessage {...messages.saved} />
                <Icon icon={['fal', 'check']} className="text-xs ml-1" />
              </span>
            ))
          ) }
        </div>
      </div>

      { onlyGraph &&
        <div className="p-2 border-t border-gray-200">
          <Chart
            ranges={ranges}
            currency={currency}
            currencySymbol={currencySymbol}
            onChange={onAmountChange}
            readOnly={readOnly}
          />
        </div>
      }

      { (!readOnly || !onlyGraph) &&
        <div className={`grid grid-cols-2 grid-flow-row auto-rows-fr md:grid-cols-none md:grid-rows-2 md:grid-flow-col md:auto-cols-fr border-t border-gray-200`}>
          { ranges.map((range, i) => (
            <Range
              key={`${id}-range-${i}`}
              currency={currency} {...range}
              onChange={(amount) => onAmountChange(i, amount)}
              readOnly={readOnly}
            />
          )) }
        </div>
      }
    </div>
  )
}

export default Rate
